import React, { useState } from 'react';
import './AssessmentScore.css';

// Type definitions for chart data
interface ChartDataItem {
  label: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

interface BarChartProps {
  data: ChartDataItem[];
  height?: number;
}

interface LineChartProps {
  data: ChartDataItem[];
  height?: number;
}

interface RadarChartProps {
  data: ChartDataItem[];
  size?: number;
}

// Chart components using SVG for beautiful visualizations
const DonutChart: React.FC<DonutChartProps> = ({ percentage, size = 120, strokeWidth = 12, color = "#F3903F" }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
  
  return (
    <div className="donut-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ 
            '--progress-dasharray': strokeDasharray,
            strokeDasharray: '0 1000',
            animation: 'drawProgress 2.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards'
          } as React.CSSProperties}
        />
      </svg>
      <div className="donut-label">
        <span className="percentage">{percentage}%</span>
        <span className="label">Score</span>
      </div>
    </div>
  );
};

const BarChart: React.FC<BarChartProps> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map((item: ChartDataItem) => item.value));
  
  return (
    <div className="bar-chart" style={{ height }}>
      <div className="bars">
        {data.map((item: ChartDataItem, index: number) => (
          <div key={index} className="bar-group">
            <div 
              className="bar" 
              style={{ 
                height: `${(item.value / maxValue) * 100}%`,
                backgroundColor: item.color || '#6B7280',
                '--bar-height': `${(item.value / maxValue) * 100}%`,
                animationDelay: `${index * 0.1}s`
              } as React.CSSProperties}
            >
              <span className="bar-value">{item.value}%</span>
            </div>
            <span className="bar-label">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LineChart: React.FC<LineChartProps> = ({ data, height = 200 }) => {
  const maxValue = Math.max(...data.map((item: ChartDataItem) => item.value));
  const minValue = Math.min(...data.map((item: ChartDataItem) => item.value));
  const range = maxValue - minValue;
  
  const points = data.map((item: ChartDataItem, index: number) => {
    const x = (index / (data.length - 1)) * 100;
    const y = 100 - ((item.value - minValue) / range) * 100;
    return `${x}%,${y}%`;
  }).join(' ');
  
  return (
    <div className="line-chart" style={{ height }}>
      <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="#F3903F"
          strokeWidth="2"
          points={points}
          style={{ 
            strokeDasharray: '1000',
            strokeDashoffset: '1000',
            animation: 'drawLine 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards'
          }}
        />
        {data.map((item: ChartDataItem, index: number) => {
          const x = (index / (data.length - 1)) * 100;
          const y = 100 - ((item.value - minValue) / range) * 100;
          return (
            <circle
              key={index}
              cx={`${x}%`}
              cy={`${y}%`}
              r="3"
              fill="#F3903F"
              style={{ 
                opacity: 0,
                animation: `pulseIn 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${1.5 + index * 0.2}s forwards`
              }}
            />
          );
        })}
      </svg>
      <div className="line-labels">
        {data.map((item: ChartDataItem, index: number) => (
          <span key={index} className="line-label">
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const RadarChart: React.FC<RadarChartProps> = ({ data, size = 200 }) => {
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size / 3;
  
  const points = data.map((item: ChartDataItem, index: number) => {
    const angle = (index / data.length) * 2 * Math.PI - Math.PI / 2;
    const value = item.value / 100;
    const x = centerX + Math.cos(angle) * radius * value;
    const y = centerY + Math.sin(angle) * radius * value;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <div className="radar-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circles */}
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius * 0.75} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius * 0.5} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius * 0.25} fill="none" stroke="#e2e8f0" strokeWidth="1" />
        
        {/* Radar area */}
        <polygon
          points={points}
          fill="rgba(243, 144, 63, 0.2)"
          stroke="#F3903F"
          strokeWidth="2"
          strokeDasharray="1000"
          style={{
            opacity: 0,
            animation: 'drawRadar 1.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards'
          }}
        />
        
        {/* Data points */}
        {data.map((item: ChartDataItem, index: number) => {
          const angle = (index / data.length) * 2 * Math.PI - Math.PI / 2;
          const value = item.value / 100;
          const x = centerX + Math.cos(angle) * radius * value;
          const y = centerY + Math.sin(angle) * radius * value;
          return (
            <circle
              key={index}
              cx={x}
              cy={y}
              r="4"
              fill="#F3903F"
              style={{
                opacity: 0,
                animation: `pulseIn 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${1.2 + index * 0.2}s forwards`
              }}
            />
          );
        })}
      </svg>
      <div className="radar-labels">
        {data.map((item: ChartDataItem, index: number) => (
          <span key={index} className="radar-label">
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const AssessmentScore: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Static assessment data
  const overallScore = 78;
  const objectiveScores = [
    { label: 'Risk Management', value: 85, color: '#F3903F' },
    { label: 'Compliance Framework', value: 72, color: '#10b981' },
    { label: 'Control Implementation', value: 68, color: '#3b82f6' },
    { label: 'Monitoring & Reporting', value: 91, color: '#8b5cf6' },
    { label: 'Incident Response', value: 76, color: '#ef4444' }
  ];

  const controlMappingData = [
    { label: 'ISO 27001', value: 82, color: '#6B7280' },
    { label: 'NIST CSF', value: 75, color: '#4B5563' },
    { label: 'GDPR', value: 88, color: '#374151' },
    { label: 'SOC 2', value: 69, color: '#9CA3AF' },
    { label: 'PCI DSS', value: 73, color: '#6B7280' }
  ];

  const standardMappingData = [
    { label: 'Security Controls', value: 85 },
    { label: 'Access Management', value: 78 },
    { label: 'Data Protection', value: 92 },
    { label: 'Incident Management', value: 71 },
    { label: 'Business Continuity', value: 84 },
    { label: 'Risk Assessment', value: 79 }
  ];

  const timelineData = [
    { label: 'Q1', value: 45 },
    { label: 'Q2', value: 58 },
    { label: 'Q3', value: 72 },
    { label: 'Q4', value: 78 }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="assessment-grid">
            <div className="score-card main-score">
              <h3>Overall Assessment Score</h3>
              <DonutChart percentage={overallScore} size={180} />
              <div className="score-details">
                <div className="score-level">
                  <span className="level-badge">Good</span>
                  <p>Your organization shows strong compliance readiness</p>
                </div>
              </div>
            </div>
            
            <div className="score-card">
              <h3>Objective Performance</h3>
              <BarChart data={objectiveScores} height={250} />
            </div>
            
            <div className="score-card">
              <h3>Progress Timeline</h3>
              <LineChart data={timelineData} height={250} />
            </div>
            
            <div className="score-card">
              <h3>Risk Assessment Matrix</h3>
              <RadarChart data={standardMappingData} size={200} />
            </div>
          </div>
        );

      case 'objectives':
        return (
          <div className="objectives-section">
            <div className="objectives-grid">
              {objectiveScores.map((objective, index) => (
                <div key={index} className="objective-card">
                  <div className="objective-header">
                    <h4>{objective.label}</h4>
                    <DonutChart percentage={objective.value} size={80} color={objective.color} />
                  </div>
                  <div className="objective-details">
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`status ${objective.value >= 80 ? 'excellent' : objective.value >= 60 ? 'good' : 'needs-improvement'}`}>
                        {objective.value >= 80 ? 'Excellent' : objective.value >= 60 ? 'Good' : 'Needs Improvement'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Priority:</span>
                      <span className="priority">High</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Last Updated:</span>
                      <span className="date">2024-01-15</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'controls':
        return (
          <div className="controls-section">
            <div className="controls-overview">
              <div className="control-stats">
                <div className="stat-card">
                  <h4>Total Controls</h4>
                  <span className="stat-number">247</span>
                </div>
                <div className="stat-card">
                  <h4>Implemented</h4>
                  <span className="stat-number">189</span>
                </div>
                <div className="stat-card">
                  <h4>In Progress</h4>
                  <span className="stat-number">34</span>
                </div>
                <div className="stat-card">
                  <h4>Not Started</h4>
                  <span className="stat-number">24</span>
                </div>
              </div>
            </div>
            
            <div className="control-mapping">
              <h3>Control Mapping by Framework</h3>
              <BarChart data={controlMappingData} height={300} />
            </div>
          </div>
        );

      case 'standards':
        return (
          <div className="standards-section">
            <div className="standards-overview">
              <RadarChart data={standardMappingData} size={300} />
            </div>
            
            <div className="standards-details">
              <h3>Standard Mapping Details</h3>
              <div className="standards-grid">
                {standardMappingData.map((standard, index) => (
                  <div key={index} className="standard-card">
                    <div className="standard-header">
                      <h4>{standard.label}</h4>
                      <span className="score">{standard.value}%</span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${standard.value}%` }}
                      ></div>
                    </div>
                    <div className="standard-metrics">
                      <div className="metric">
                        <span className="metric-label">Controls:</span>
                        <span className="metric-value">24</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Compliant:</span>
                        <span className="metric-value">18</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Gaps:</span>
                        <span className="metric-value">6</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="assessment-score-page">
      <div className="assessment-header">
        <h1>Assessment Score Dashboard</h1>
        <p>Comprehensive evaluation of your compliance and risk management posture</p>
      </div>

      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-button ${activeTab === 'objectives' ? 'active' : ''}`}
          onClick={() => setActiveTab('objectives')}
        >
          Objectives
        </button>
        <button 
          className={`tab-button ${activeTab === 'controls' ? 'active' : ''}`}
          onClick={() => setActiveTab('controls')}
        >
          Control Mapping
        </button>
        <button 
          className={`tab-button ${activeTab === 'standards' ? 'active' : ''}`}
          onClick={() => setActiveTab('standards')}
        >
          Standard Mapping
        </button>
      </div>

      <div className="assessment-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default AssessmentScore; 